import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, combineLatest, filter, map, take } from 'rxjs';
import { CustomClaimService } from '../services/custom-claim.service';
import { AuthService } from '../services/auth.service';

/**
 * Super Admin Guard
 * Protects routes that require superAdmin claim
 * Waits for Firebase auth initialization before checking
 *
 * @see agent-os/specs/2026-01-25-reset-integration-data/spec.md
 */
@Injectable({
  providedIn: 'root',
})
export class SuperAdminGuard {
  constructor(
    private customClaimService: CustomClaimService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    // Wait for Firebase auth to finish loading before checking claims
    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$,
    ]).pipe(
      filter(([loading]) => !loading), // Wait until loading is complete
      take(1),
      map(([_, user]) => {
        // User is not authenticated
        if (!user) {
          return this.router.createUrlTree(['/login']);
        }

        // Check if user is super admin
        if (this.customClaimService.isSuperAdmin()) {
          return true;
        }

        // Not a super admin - redirect to dashboard
        console.warn('SuperAdminGuard: Access denied. User is not a super admin.');
        return this.router.createUrlTree(['/dashboard']);
      })
    );
  }

  canActivateChild(): Observable<boolean | UrlTree> {
    return this.canActivate();
  }
}
